"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; 
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import { StatsCard } from "../../../components/stats-card";
import Pagination from "../../../components/Pagination";
import Button from "../../../components/Button";
import { Table } from "../../../components/Table";
import Dropdown from "../../../components/Dropdown";
import SearchBar from "../../../components/SearchBar";
import styles from "../css/userManagement.module.css";
import { getBranches, getUsers, getRoles, toggleUserActive } from "../../../api/userManagement/userManagementApi";
import api from "../../../api.js";

export default function UserManagementView() {
  const navigate = useNavigate(); 
  const [selectedBranchCode, setSelectedBranchCode] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return window.sessionStorage.getItem("userManagementSelectedBranchCode") || "";
    } catch {
      return "";
    }
  });
  const [branchList, setBranchList] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("All Roles");
  const [roles, setRoles] = useState([]); // string[] role names
  const [roleNameById, setRoleNameById] = useState(() => new Map());
  const [toggling, setToggling] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const getUserRoleNames = (u) => {
    if (Array.isArray(u?.rolesNames) && u.rolesNames.length) return u.rolesNames;

    const ids = Array.isArray(u?.roles)
      ? u.roles
      : Array.isArray(u?.roleids)
        ? u.roleids
        : [];
    if (ids.length && roleNameById && typeof roleNameById.get === "function") {
      const mapped = ids
        .map((rid) => roleNameById.get(Number(rid)))
        .filter((v) => typeof v === "string" && v.trim().length > 0);
      if (mapped.length) return mapped;
    }

    if (typeof u?.role === "string" && u.role.trim()) return [u.role];
    return [];
  };
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const data = await getBranches();
        setBranchList(data);

        const hasSelected = !!selectedBranchCode;
        const fallbackBranchCode = data.length > 0 ? data[0].branchcode : "";
        const nextBranchCode = hasSelected
          ? selectedBranchCode
          : fallbackBranchCode;

        if (nextBranchCode) {
          setSelectedBranchCode(nextBranchCode);
          try {
            if (typeof window !== "undefined") {
              window.sessionStorage.setItem(
                "userManagementSelectedBranchCode",
                nextBranchCode
              );
            }
          } catch {}
        }
      } catch (err) {
        console.error("Failed to load branches:", err);
      }
    };
    fetchBranches();
  }, []);
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (!selectedBranchCode) return;
        const data = await getUsers(selectedBranchCode);
        setUsers(data);
      } catch (err) {
        console.error("Failed to load users:", err);
      }
    };
    fetchUsers();
  }, [selectedBranchCode]);
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get("/api/users/roles-detailed");
        const data = res?.data;
        const map = new Map();
        const names = [];
        (Array.isArray(data) ? data : []).forEach((r) => {
          const id = Number(r?.roleid);
          const name = r?.rolename;
          if (Number.isFinite(id) && typeof name === "string" && name.trim()) {
            map.set(id, name);
            names.push(name);
          }
        });
        setRoleNameById(map);
        setRoles(Array.from(new Set(names)));
      } catch (err) {
        console.error("Failed to load roles:", err);
        setRoles([]);
        setRoleNameById(new Map());
      }
    };
    fetchRoles();
  }, []);

  const columns = [
    { header: "Name", accessor: "name" },
    { header: "Email", accessor: "email" },
    {
      header: "Role",
      accessor: "role",
      render: (_, row) => {
        const roleNames = getUserRoleNames(row);
        const getRoleClass = (role) => {
          const r = String(role || "").toLowerCase();
          let cls;
          if (r.includes("responsible pharmacist")) {
            cls = styles["role-blue"];
          } else if (r.includes("pcdt")) {
            cls = styles["role-green"];
          } else if (r.includes("dispensary assistant")) {
            cls = styles["role-teal"];
          } else if (r.includes("front shop")) {
            cls = styles["role-purple"];
          } else if (r.includes("nurse")) {
            cls = styles["role-red"];
          } else if (r.includes("director")) {
            cls = styles["role-black"];
          } else {
            let hash = 0;
            for (let i = 0; i < r.length; i++) {
              hash = (hash << 5) - hash + r.charCodeAt(i);
              hash |= 0;
            }
            const idx = Math.abs(hash) % 9;
            cls = styles[`role-color${idx}`] || styles["role-color0"];
          }
          return cls;
        };

        if (!roleNames.length) {
          return <span className={`${styles.role} ${styles["role-color0"] || ""}`.trim()}>No Role</span>;
        }

        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            {roleNames.map((role) => {
              const cls = getRoleClass(role);
              return (
                <span key={role} className={`${styles.role} ${cls || ""}`.trim()}>
                  {role}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      header: "Action",
      accessor: "action",
      render: (_, row) => (
        <div className={styles.actionButtons}>
          <Button
            variant="primary"
            onClick={() =>
              navigate("/edit-user", {
                state: {
                  email: row.email,
                  employee_number: row.employee_number,
                },
              })
            }
          >
            Edit
          </Button>
          <Button
            variant={row.isActive ? "cancel" : "approve"}
            onClick={() => handleToggleDisable(row)}
            disabled={!!toggling[row.email || row.employee_number]}
          >
            {row.isActive ? "Disable" : "Enable"}
          </Button>
        </div>
      ),
    },
  ];

  const handleToggleDisable = async (row) => {
    const key = row.email || row.employee_number;
    const nextActive = !row.isActive;
    const previous = users;
    // Optimistic update by matching either email or employee_number
    setUsers((prev) => prev.map(u => {
      const match = (row.email && u.email === row.email) || (!row.email && u.employee_number === row.employee_number);
      return match ? { ...u, isActive: nextActive, disabled: !nextActive } : u;
    }));
    try {
      setToggling((p) => ({ ...p, [key]: true }));
      if (row.email) {
        await toggleUserActive(row.email, nextActive);
      } else if (row.employee_number) {
        await api.patch(`/api/users/employees/by-employee-number/${encodeURIComponent(row.employee_number)}/toggle`, { isActive: nextActive });
      } else {
        throw new Error("No identifier to toggle user");
      }
    } catch (err) {
      console.error("Failed to toggle user:", err);
      setUsers(previous);
      try {
        const refreshed = await getUsers(selectedBranchCode);
        setUsers(refreshed);
      } catch (_) {}
    } finally {
      setToggling((p) => ({ ...p, [key]: false }));
    }
  };

  // Filter users based on search query and selected role
  const filteredUsers = users.filter((u) => {
    const roleNames = getUserRoleNames(u);
    const roleText = roleNames.join(" ");
    const matchesSearch =
      String(u.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(u.email || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      roleText.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole =
      selectedRole === "All Roles" ||
      roleNames.includes(selectedRole) ||
      String(u.role || "") === selectedRole;
    return matchesSearch && matchesRole;
  });

  const rowsPerPage = 5;
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage));
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedRole, selectedBranchCode, users.length]);

  const roleCounts = users.reduce(
    (acc, u) => {
      const r = getUserRoleNames(u).join(" ").toLowerCase();
      if (r.includes("pcdt")) acc.pcdt += 1;
      else if (r.includes("responsible pharmacist")) acc.rp += 1;
      if (r.includes("dispensary assistant")) acc.dispensary += 1;
      if (r.includes("front shop")) acc.frontshop += 1;
      if (r.includes("nurse")) acc.nurses += 1;
      return acc;
    },
    { rp: 0, pcdt: 0, dispensary: 0, frontshop: 0, nurses: 0 }
  );

  return (
    <div className={styles.app}>
      <div className={styles.bodyWrapper}>
        <div className={styles.mainContainer}>
          <Navbar />
          <main className={styles.content}>

            {/* Branch Dropdown */}
            <div className={styles.branchDropdownContainer}>
              <span className={styles.selectedBranchLabel}>Selected Branch:</span>
              <Dropdown 
                options={branchList.map(b => b.branchname)} // display names
                value={branchList.find(b => b.branchcode === selectedBranchCode)?.branchname || ""}
                onChange={(branchName) => {
                  const branch = branchList.find(b => b.branchname === branchName);
                  const code = branch?.branchcode || "";
                  setSelectedBranchCode(code);
                  try {
                    if (typeof window !== "undefined") {
                      window.sessionStorage.setItem(
                        "userManagementSelectedBranchCode",
                        code
                      );
                    }
                  } catch {}
                }}
                className={styles.compactDropdown} 
              />
            </div>

            {/* Stats Cards */}
            <div className={styles.metricsGrid}>
              <StatsCard title="Responsible Pharmacists" value={roleCounts.rp} icon="users" />
              <StatsCard title="PCDT/RP" value={roleCounts.pcdt} icon="users" />
              <StatsCard title="Dispensary Assistants" value={roleCounts.dispensary} icon="users" />
              <StatsCard title="Front Shop Sales" value={roleCounts.frontshop} icon="users" />
              <StatsCard title="Nurses" value={roleCounts.nurses} icon="users" />
            </div>

            {/* Users Section */}
            <div className={styles.usermain}>
              <div className={styles.usersHeader}>
                <h2>Users</h2>
                <div className={styles.usersHeaderActions}>
                  <SearchBar value={searchQuery} onChange={setSearchQuery} />
                  <Dropdown
                    options={["All Roles", ...roles]}
                    value={selectedRole}
                    onChange={setSelectedRole}
                    className={styles.compactDropdown}
                  />
                  <Button variant="primary" onClick={() => navigate("/add-user", { state: { branchcode: selectedBranchCode } })}>Add User</Button>
                </div>
              </div>

              <Table columns={columns} data={paginatedUsers} />

              <div className={styles.paginationWrapper}>
                <Pagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </div>
  );
}
